/**
 * HintView — Parsed Feedback view for runs whose prompt declares
 * `outputSchemaType: 'HintV1'`. Hints are not typically the primary thing
 * shown on a Submission Detail page, but the dispatcher includes this view
 * so any hint-producing run rendered here surfaces the LLM's hint payload
 * cleanly instead of falling through to "no parser".
 *
 * The wire shape lives on `feedback.llmAssessment` for hint runs (re-using
 * the Feedback container loosely). Most fields are emitted by the hint
 * prompt's structured-output schema (see Backend/functions/src/prompt/typeSchemas.ts).
 */
import { LatexText } from '@shared-ui/latex';
import type { Feedback } from '@padcanvas/shared';

interface Props {
    feedback: Feedback;
}

interface HintPayload {
    hint?: string;
    detected_state?: string;
    detected_issue?: string;
    difficulty?: string;
    /** Current schema (second-person, student-facing). */
    your_progress?: string;
    /** Legacy schema (third-person) — kept so historical hints still display. */
    student_progress?: string;
    confidence?: number;
}

export function HintView({ feedback }: Props) {
    // The hint payload is snake_case from the LLM's structured output. It can
    // arrive two ways: nested under `feedback.llmAssessment` (synthesized from
    // raw via `hintFeedbackFromRaw`), or flat on `feedback` itself when the run
    // persisted the parsed hint as `assessmentDetails` (the parsed hint is the
    // whole object). Read `llmAssessment` first, else fall back to the feedback
    // object directly — otherwise a flat payload renders as "No hint".
    const payload = (feedback.llmAssessment ?? feedback) as unknown as HintPayload;
    const meta: Array<[string, string | undefined]> = [
        ['Detected state', payload.detected_state],
        ['Detected issue', payload.detected_issue],
        ['Difficulty', payload.difficulty],
        ['Your progress', payload.your_progress ?? payload.student_progress],
        ['Confidence', typeof payload.confidence === 'number' ? payload.confidence.toFixed(2) : undefined],
    ];

    return (
        <div className="feedback-content">
            <div className="feedback-sections" style={{ marginBottom: 12 }}>
                <div className="feedback-section">
                    <h4>Hint</h4>
                    {payload.hint
                        ? <p><LatexText text={payload.hint} /></p>
                        : <p className="empty">No hint</p>}
                </div>
            </div>
            <div className="feedback-sections">
                {meta.filter(([, v]) => v !== undefined && v !== '').map(([label, value]) => (
                    <div key={label} className="feedback-section">
                        <h4>{label}</h4>
                        <p>{value}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}
